import React from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
 
const Unauthorized: React.FC = () => {
  const navigate = useNavigate();
 
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#fafafa',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 4, sm: 6, md: 8 },
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: { xs: '100%', sm: 560, md: 700 },
          width: '100%',
          px: { xs: 1, sm: 3, md: 4 },
        }}
      >
        {/* Access Denied Icon */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            mb: { xs: 3, sm: 4, md: 5 },
          }}
        >
          <Box
            sx={{
              width: { xs: 130, sm: 160, md: 200, lg: 220 },
              height: { xs: 130, sm: 160, md: 200, lg: 220 },
              borderRadius: '50%',
              bgcolor: 'rgba(244, 67, 54, 0.05)',
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {/* Lock Shackle */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 15, sm: 20, md: 26, lg: 30 },
                width: { xs: 32, sm: 40, md: 50, lg: 55 },
                height: { xs: 42, sm: 52, md: 64, lg: 70 },
                border: {
                  xs: '8px solid #3f3f46',
                  sm: '10px solid #3f3f46',
                  md: '14px solid #3f3f46',
                  lg: '16px solid #3f3f46',
                },
                borderBottom: 'none',
                borderRadius: '50px 50px 0 0',
              }}
            />
 
            {/* Lock Body */}
            <Box
              sx={{
                width: { xs: 72, sm: 90, md: 110, lg: 120 },
                height: { xs: 56, sm: 70, md: 84, lg: 90 },
                bgcolor: '#ef5350',
                borderRadius: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: { xs: 3, sm: 4, md: 5 },
                zIndex: 3,
                boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
              }}
            >
              <CloseRoundedIcon
                sx={{
                  color: '#fff',
                  fontSize: {
                    xs: 32,
                    sm: 42,
                    md: 56,
                    lg: 64,
                  },
                }}
              />
            </Box>
 
            {/* Decorative × left */}
            <Box
              sx={{
                position: 'absolute',
                top: '20%',
                left: { xs: '-5%', sm: '-10%' },
                color: '#d1d5db',
                fontSize: { xs: 18, sm: 22, md: 24 },
                lineHeight: 1,
              }}
            >
              ×
            </Box>
 
            {/* Decorative × right */}
            <Box
              sx={{
                position: 'absolute',
                top: '25%',
                right: { xs: '-5%', sm: '-10%' },
                color: '#d1d5db',
                fontSize: { xs: 18, sm: 22, md: 24 },
                lineHeight: 1,
              }}
            >
              ×
            </Box>
 
            {/* Decorative circle bottom-left */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '20%',
                left: { xs: '-2%', sm: '-5%' },
                width: { xs: 8, sm: 10, md: 12 },
                height: { xs: 8, sm: 10, md: 12 },
                border: '2px solid #d1d5db',
                borderRadius: '50%',
              }}
            />
 
            {/* Decorative circle bottom-right */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '15%',
                right: { xs: '-2%', sm: '-5%' },
                width: { xs: 8, sm: 10, md: 12 },
                height: { xs: 8, sm: 10, md: 12 },
                border: '2px solid #d1d5db',
                borderRadius: '50%',
              }}
            />
          </Box>
        </Box>
 
        {/* Heading */}
        <Typography
          component="h1"
          sx={{
            fontWeight: 700,
            color: '#111827',
            mb: { xs: 1.5, sm: 2 },
            fontSize: {
              xs: '1.75rem',
              sm: '2.5rem',
              md: '3.25rem',
              lg: '3.75rem',
            },
            lineHeight: { xs: 1.2, sm: 1.15 },
          }}
        >
          Access Denied
        </Typography>
 
        {/* Description */}
        <Typography
          sx={{
            color: 'text.secondary',
            maxWidth: { xs: '100%', sm: 480, md: 600 },
            mx: 'auto',
            lineHeight: { xs: 1.6, sm: 1.5 },
            px: { xs: 1, sm: 2 },
            fontSize: {
              xs: '0.9rem',
              sm: '1.05rem',
              md: '1.25rem',
              lg: '1.5rem',
            },
          }}
        >
          You don't have permission to access this page
          <Box
            component="span"
            sx={{
              display: { xs: 'inline', sm: 'block' },
            }}
          >
            {' '}or the requested resource.
          </Box>
        </Typography>
 
        {/* Divider */}
        <Divider
          sx={{
            width: {
              xs: '70%',
              sm: 240,
              md: 300,
              lg: 320,
            },
            mx: 'auto',
            my: { xs: 3, sm: 4 },
          }}
        />
 
        {/* Help Text */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0.75, sm: 1 },
            color: 'text.secondary',
            mb: { xs: 3.5, sm: 5 },
            px: { xs: 1, sm: 2 },
            flexWrap: 'wrap',
          }}
        >
          <HelpOutlineIcon
            sx={{
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          />
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              fontSize: { xs: '0.85rem', sm: '1rem' },
            }}
          >
            If you believe this is a mistake, please contact your administrator.
          </Typography>
        </Box>
 
        {/* Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: { xs: 1.5, sm: 2 },
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            alignItems: 'center',
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/dashboard')}
            sx={{
              width: {
                xs: '100%',
                sm: 'auto',
              },
              maxWidth: { xs: '100%', sm: 280, md: 300 },
              minWidth: { sm: 160, md: 180 },
              py: { xs: 1.25, sm: 1.5 },
              px: { xs: 3, sm: 4 },
              borderRadius: 2,
              textTransform: 'none',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'rgba(150, 193, 33, 0.75)',
                color: '#fff',
              },
            }}
          >
            Back to Home
          </Button>
 
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate(-1)}
            sx={{
              width: {
                xs: '100%',
                sm: 'auto',
              },
              maxWidth: { xs: '100%', sm: 280, md: 300 },
              minWidth: { sm: 160, md: 180 },
              py: { xs: 1.25, sm: 1.5 },
              px: { xs: 3, sm: 4 },
              borderRadius: 2,
              textTransform: 'none',
              fontSize: { xs: '0.9rem', sm: '1rem' },
            }}
          >
            Go Back
          </Button>
        </Box>
      </Box>
    </Container>
  );
};
 
export default Unauthorized;